import { jsx, jsxs } from "react/jsx-runtime";
import MBox from "@mui/material/Box";
import MFormHelperText from "@mui/material/FormHelperText";
import MInputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import styled from "@mui/material/styles/styled";
import cn from "classnames";
import isFunction from "lodash/isFunction";
import toNumber from "lodash/toNumber";
import React from "react";
import { useParentBg } from "../../shared/hooks/current-color";
import { DION_SCROLLBAR } from "../../theme";
import Box from "../Box";
import { Icon } from "../Icon";
import { useTheme } from "../ThemeProvider";
const LABEL_SX = {
  display: "flex",
  maxWidth: "100%",
  justifyContent: "space-between",
  overflow: "hidden"
};
const MULTILINE_PADDING = {
  l: 12,
  m: 8
};
const ICON_PADDING = 12;
var EInputSize = /* @__PURE__ */ ((EInputSize2) => {
  EInputSize2["L"] = "l";
  EInputSize2["M"] = "m";
  return EInputSize2;
})(EInputSize || {});
var EInputStatus = /* @__PURE__ */ ((EInputStatus2) => {
  EInputStatus2["DEFAULT"] = "default";
  EInputStatus2["SUCCESS"] = "success";
  EInputStatus2["ERROR"] = "error";
  EInputStatus2["DISABLED"] = "disabled";
  EInputStatus2["READONLY"] = "readOnly";
  return EInputStatus2;
})(EInputStatus || {});
const LABEL_COLOR_BY_STATUS = {
  ["readOnly" /* READONLY */]: "additional",
  ["disabled" /* DISABLED */]: "lowAccent",
  ["default" /* DEFAULT */]: "contrast",
  ["error" /* ERROR */]: "contrast",
  ["success" /* SUCCESS */]: "contrast"
};
const COUNTER_COLOR_BY_STATUS = {
  ["readOnly" /* READONLY */]: "additional",
  ["disabled" /* DISABLED */]: "lowAccent",
  ["default" /* DEFAULT */]: "additional",
  ["error" /* ERROR */]: "errorContent",
  ["success" /* SUCCESS */]: "successContent"
};
const ADDITIONAL_TEXT_COLOR_BY_STATUS = {
  ["readOnly" /* READONLY */]: "additional",
  ["disabled" /* DISABLED */]: "lowAccent",
  ["default" /* DEFAULT */]: "additional",
  ["error" /* ERROR */]: "errorContent",
  ["success" /* SUCCESS */]: "successContent"
};
const END_ICON_COLOR_BY_STATUS = {
  ["readOnly" /* READONLY */]: "contrast",
  ["disabled" /* DISABLED */]: "lowAccent",
  ["default" /* DEFAULT */]: "contrast",
  ["error" /* ERROR */]: "errorContent",
  ["success" /* SUCCESS */]: "successContent"
};
const CustomOutlinedInput = styled(OutlinedInput)(({
  theme,
  inputSize = "m" /* M */,
  color,
  status,
  startAdornment,
  endAdornment,
  refHeight
}) => {
  const palette = theme.palette;
  const inputMinHeight = `${theme.input.size[inputSize]}px`;
  const calcHeight = refHeight + MULTILINE_PADDING[inputSize] * 2;
  const inputHeight = calcHeight > theme.input.size[inputSize] ? `${calcHeight}px` : inputMinHeight;
  const defaultBgColor = palette[color].transparent;
  const disabledBgColor = palette[color].disable;
  const bgColor = status === "disabled" /* DISABLED */ ? disabledBgColor : defaultBgColor;
  const inputPaddingLeft = startAdornment ? `${theme.input.padding[inputSize]?.icon}px` : `${theme.input.padding[inputSize]?.content}px`;
  const inputPaddingRight = endAdornment ? `${theme.input.padding[inputSize]?.icon}px` : `${theme.input.padding[inputSize]?.content}px`;
  const inputBorderRadius = `${theme.input.borderRadius[inputSize]}px`;
  const inputBorder = status === "error" /* ERROR */ || status === "success" /* SUCCESS */ ? `1px solid ${palette[status].main}` : "none";
  const mainColor = palette[color].contrast;
  const mainTypography = theme.typography[theme.input.typography[inputSize]?.mainText];
  const placeholderColor = palette[color].lowContrast;
  const placeholderTypography = theme.typography[theme.input.typography[inputSize]?.placeholder];
  return {
    height: inputHeight,
    minHeight: inputMinHeight,
    backgroundColor: bgColor,
    borderRadius: inputBorderRadius,
    paddingRight: inputPaddingRight,
    paddingLeft: inputPaddingLeft,
    paddingTop: `${MULTILINE_PADDING[inputSize]}px`,
    paddingBottom: `${MULTILINE_PADDING[inputSize]}px`,
    width: "100%",
    "& .MuiOutlinedInput-input": {
      marginRight: "-4px",
      ...DION_SCROLLBAR,
      color: mainColor,
      textOverflow: "ellipsis",
      paddingTop: "0px",
      paddingBottom: "0px",
      paddingLeft: "0px",
      ...mainTypography,
      "&::placeholder": {
        color: placeholderColor,
        ...placeholderTypography
      }
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: inputBorder
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: inputBorder
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: inputBorder
    }
  };
});
const CustomInputLabel = styled(MFormHelperText)(({ theme, status, color, inputSize }) => {
  const typography = theme.typography[theme.input.typography[inputSize].label];
  const labelColor = theme.palette[color][LABEL_COLOR_BY_STATUS[status]];
  return {
    "&.MuiFormHelperText-root": {
      ...typography,
      flexShrink: 1,
      whiteSpace: "no-wrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      margin: 0,
      color: labelColor
    }
  };
});
const CustomInputCounter = styled(MFormHelperText)(({ theme, status, color, inputSize }) => {
  const typography = theme.typography[theme.input.typography[inputSize].counter];
  const counterColor = theme.palette[color][COUNTER_COLOR_BY_STATUS[status]];
  return {
    "&.MuiFormHelperText-root": {
      ...typography,
      color: counterColor,
      margin: 0,
      flexGrow: 0,
      marginLeft: "8px"
    }
  };
});
const CustomInputAdditionalText = styled(MFormHelperText)(({ theme, status, color, inputSize }) => {
  const typography = theme.typography[theme.input.typography[inputSize].additional];
  const textColor = theme.palette[color][ADDITIONAL_TEXT_COLOR_BY_STATUS[status]];
  const textMarginTop = `${theme.input.margin[inputSize].bottom}px`;
  return {
    "&.MuiFormHelperText-root": {
      ...typography,
      marginTop: textMarginTop,
      overflow: "hidden",
      textOverflow: "ellipsis",
      color: textColor
    }
  };
});
const CustomInputAdornment = styled(MInputAdornment)(({
  theme,
  status,
  color,
  position,
  onClick
}) => {
  const palette = theme.palette;
  const isStartIcon = position === "start";
  const startIconColor = status === "disabled" /* DISABLED */ ? palette[color].lowAccent : palette[color].lowContrast;
  const endIconColor = theme.palette[color][END_ICON_COLOR_BY_STATUS[status]];
  return {
    "&.MuiInputAdornment-root": {
      color: isStartIcon ? startIconColor : endIconColor,
      cursor: isFunction(onClick) ? "pointer" : "inherit"
    }
  };
});
const IconAdornmentContainer = styled(MBox)(({
  position
}) => {
  return {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: position,
    height: "100%",
    paddingRight: "0px",
    paddingTop: `${ICON_PADDING}px`,
    paddingBottom: `${ICON_PADDING}px`
  };
});
const IconAdornment = (props) => {
  const {
    status = "default" /* DEFAULT */,
    icon,
    iconPosition,
    onClick,
    color,
    verticalIconPosition
  } = props;
  return /* @__PURE__ */ jsx(
    CustomInputAdornment,
    {
      position: iconPosition,
      status,
      onClick,
      color,
      verticalIconPosition,
      children: /* @__PURE__ */ jsx(Icon, { icon, size: "l" })
    }
  );
};
const InputContainer = styled(Box)(({
  width,
  fullWidth
}) => {
  return {
    width: fullWidth ? "100%" : `${width}px`
  };
});
const Input = React.forwardRef((props, ref) => {
  const {
    name,
    size = "m" /* M */,
    placeholder,
    label,
    additionalText,
    status = "default" /* DEFAULT */,
    maxLength,
    endIcon,
    startIcon,
    value: rawValue,
    width,
    fullWidth,
    onChange,
    onEndIconClick,
    onStartIconClick,
    startIconPosition = "start",
    endIconPosition = "end",
    onFocus,
    onBlur,
    onKeyUp,
    color: rawColor,
    inputRef,
    multiline,
    minRows,
    maxRows,
    form,
    type,
    autoFocus,
    autoComplete,
    startAdornment,
    className
  } = props;
  const theme = useTheme();
  const parentBg = useParentBg();
  const color = rawColor ?? parentBg;
  const isTooLong = maxLength && rawValue?.length > maxLength;
  const value = isTooLong ? rawValue.slice(0, maxLength) : rawValue ?? "";
  const valueLength = value?.length ?? 0;
  const showCounter = maxLength && toNumber(maxLength) > 0;
  const classes = cn("input-wrapper", className);
  const handleChange = React.useCallback((e) => {
    const { value: value2 } = e.target;
    const isTooLong2 = maxLength && value2?.length > maxLength;
    e.target.value = isTooLong2 ? value2.slice(0, maxLength) : value2;
    if (isFunction(onChange))
      onChange(e);
  }, [maxLength, onChange]);
  const textAreaRef = React.useRef();
  const [textAreaHeight, setTextAreaHeight] = React.useState(null);
  const [forceUpdate, setForceUpdate] = React.useState(false);
  const inputProps = React.useMemo(() => ({ ref: textAreaRef, name, form, type }), [textAreaRef, name, form, type]);
  const labelBottomMargin = `${theme.input.margin[size ?? "m" /* M */].top}px`;
  React.useEffect(() => {
    setTextAreaHeight(textAreaRef.current.offsetHeight);
  }, [textAreaRef?.current?.offsetHeight]);
  React.useEffect(() => {
    setForceUpdate(!forceUpdate);
  }, [multiline, minRows]);
  const handleBlur = React.useCallback((event) => {
    const relatedTarget = event.relatedTarget || event.nativeEvent.relatedTarget;
    if (ref?.current?.contains(relatedTarget)) {
      return;
    }
    if (typeof onBlur === "function") {
      onBlur(event);
    }
  }, [onBlur]);
  const handleMouseDown = React.useCallback((event) => {
    if (event.target === event.currentTarget) {
      event.preventDefault();
    }
  }, []);
  return /* @__PURE__ */ jsxs(
    InputContainer,
    {
      width,
      fullWidth,
      ref,
      className: classes,
      children: [
        (showCounter || label) && /* @__PURE__ */ jsxs(Box, { sx: LABEL_SX, marginBottom: labelBottomMargin, className: "label-container", children: [
          /* @__PURE__ */ jsx(CustomInputLabel, { color, status, inputSize: size, children: label }),
          showCounter ? /* @__PURE__ */ jsx(CustomInputCounter, { color, status, inputSize: size, children: `${valueLength}/${maxLength}` }) : null
        ] }),
        /* @__PURE__ */ jsx(
          CustomOutlinedInput,
          {
            name,
            color,
            status,
            placeholder,
            value,
            disabled: status === "disabled" /* DISABLED */,
            onChange: handleChange,
            onFocus,
            onBlur: handleBlur,
            onKeyUp,
            onMouseDown: handleMouseDown,
            readOnly: status === "readOnly" /* READONLY */,
            inputSize: size,
            inputRef,
            multiline,
            minRows,
            maxRows,
            refHeight: textAreaHeight,
            inputProps,
            autoComplete,
            autoFocus,
            startAdornment: startAdornment ?? (startIcon && /* @__PURE__ */ jsx(
              IconAdornmentContainer,
              {
                position: startIconPosition,
                multiline,
                children: /* @__PURE__ */ jsx(
                  IconAdornment,
                  {
                    color,
                    onClick: onStartIconClick,
                    status,
                    icon: startIcon,
                    iconPosition: "start",
                    verticalIconPosition: startIconPosition
                  }
                )
              }
            )),
            endAdornment: endIcon && /* @__PURE__ */ jsx(
              IconAdornmentContainer,
              {
                position: endIconPosition,
                children: /* @__PURE__ */ jsx(
                  IconAdornment,
                  {
                    color,
                    onClick: onEndIconClick,
                    status,
                    icon: endIcon,
                    iconPosition: "end",
                    verticalIconPosition: endIconPosition
                  }
                )
              }
            )
          }
        ),
        additionalText && /* @__PURE__ */ jsx(CustomInputAdditionalText, { color, status, inputSize: size, children: additionalText })
      ]
    }
  );
});
var Input_default = Input;
export {
  EInputSize,
  EInputStatus,
  Input_default as default
};
